import React, { useState, useEffect } from 'react';
import ColorPicker from './ColorPicker';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import RichTextEditor from './RichTextEditor';
import CustomTableEditor from './CustomTableEditor';
import CompositeSection from './CompositeSection';
import FindingsWalkthroughConfig from './FindingsWalkthroughConfig';
import RevalidationSummaryConfig from './RevalidationSummaryConfig';
import OwaspFindingsConfig from './OwaspFindingsConfig';
import FindingCardsConfig from './FindingCardsConfig';
import { Input, TextArea } from '../Common';
import { FONT_OPTIONS_INHERIT } from './fontOptions';
import { METRIC_TILE_LABEL_GAP, METRIC_TILE_ALIGNMENTS, metricTileAlign } from './metricTileDefaults';
import { RISK_RATING_LABELS, riskRatingDefaultRows, riskRatingColor } from './riskRatingDefaults';
import { SCOPE_TABLE_LABELS, SCOPE_TABLE_EMPTY, FINDINGS_SUMMARY_LABELS, resolveColumnLabels, findingsSummaryColumns } from './tableLabelDefaults';

const SECTION_ICONS = {
  'title-page': '📄', 'table-of-contents': '📑', 'rich-text': '📝',
  'severity-dashboard': '📊', 'risk-rating-table': '⚖️', 'scope-table': '🎯',
  'findings-summary': '📋', 'findings-detail': '🔍', 'page-break': '➖',
  'heading': 'H', 'text-block': '\u00b6', 'custom-table': '\u25a6',
  'image': '\ud83d\uddbc\ufe0f', 'severity-chart': '\ud83e\udd67', 'horizontal-line': '─', 'composite': '\ud83e\udde9',
  'spacer': '↕', 'version-history': '🕑', 'metric-tiles': '▤',
  'findings-walkthrough': '🧾', 'owasp-findings': '🛡️', 'finding-cards': '🗂️'
};

function HeadingInput({ config, update }) {
  return (
    <>
      <Input label="Section Heading" value={config.heading || ''} onChange={e => update('heading', e.target.value)} style={{ marginBottom: 0 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Heading Align:</span>
        {['left', 'center', 'right'].map(a => (
          <button key={a} onClick={() => update('headingAlign', a)} style={{
            padding: '4px 12px', fontSize: 12, fontWeight: 600, textTransform: 'capitalize',
            background: (config.headingAlign || 'left') === a ? 'var(--primary)' : 'var(--bg-input)',
            color: (config.headingAlign || 'left') === a ? '#fff' : 'var(--text)',
            border: '1px solid var(--border)', borderRadius: 'var(--radius)', cursor: 'pointer'
          }}>{a}</button>
        ))}
      </div>
    </>
  );
}

/**
 * Extra columns on the findings summary table, beyond the built-in ones.
 *
 * A column is a name plus an optional value. Leaving the value empty is a
 * supported choice, not an incomplete one: a named but blank column gives the
 * tester a ruled space to fill in after export — remediation owner, target
 * date, sign-off. A value may instead hold variables, including per-finding
 * ones that differ row to row.
 */
/**
 * The Risk Rating table's own content.
 *
 * The bands used to be fixed in the renderers, so a template could rename the
 * section and set its width but not say what any band meant -- which is the one
 * thing a client's own risk language actually needs. Everything printed is
 * editable here: the four column headings, and each band's name, score range,
 * description and fill.
 *
 * An untouched section carries no rows of its own and keeps printing the
 * standard scale, so a template saved before this existed is unchanged. The
 * first edit writes the whole set down, which is also what lets Reset put it
 * back by clearing the key rather than by restating it.
 */
function RiskRatingRows({ config, update, updateMany }) {
  const custom = Array.isArray(config.ratings) ? config.ratings : null;
  const rows = custom || riskRatingDefaultRows();
  const labels = { ...RISK_RATING_LABELS, ...(config.columnLabels || {}) };

  const setRows = next => update('ratings', next);
  const edit = (i, key, value) => setRows(rows.map((r, n) => (n === i ? { ...r, [key]: value } : r)));
  const setLabel = (key, value) => update('columnLabels', { ...(config.columnLabels || {}), [key]: value });

  const field = {
    padding: '6px 10px', fontSize: 13, borderRadius: 6, border: '1px solid var(--border)',
    background: 'var(--bg-card)', color: 'var(--text)', fontFamily: 'inherit', boxSizing: 'border-box',
  };

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>Column Headings</span>
        {custom && (
          <button type="button" title="Put the standard CVSS scale back"
            onClick={() => updateMany({ ratings: undefined, columnLabels: undefined })}
            style={{ padding: '4px 10px', fontSize: 12, fontWeight: 600, borderRadius: 'var(--radius)',
                     background: 'var(--bg-input)', color: 'var(--text-secondary)',
                     border: '1px solid var(--border)', cursor: 'pointer', fontFamily: 'inherit' }}>
            Reset to standard
          </button>
        )}
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        <input value={labels.index} aria-label="First column heading" onChange={e => setLabel('index', e.target.value)}
          style={{ ...field, flex: '0 0 48px', textAlign: 'center' }} />
        <input value={labels.rating} aria-label="Rating column heading" onChange={e => setLabel('rating', e.target.value)}
          style={{ ...field, flex: '0 0 26%' }} />
        <input value={labels.score} aria-label="Score column heading" onChange={e => setLabel('score', e.target.value)}
          style={{ ...field, flex: '0 0 22%' }} />
        <input value={labels.description} aria-label="Description column heading" onChange={e => setLabel('description', e.target.value)}
          style={{ ...field, flex: 1 }} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>Rating Bands</span>
        <button type="button"
          onClick={() => setRows([...rows, { severity: '', score: '', description: '' }])}
          style={{ padding: '4px 10px', fontSize: 12, fontWeight: 600, borderRadius: 'var(--radius)',
                   background: 'var(--bg-input)', color: 'var(--text-secondary)',
                   border: '1px solid var(--border)', cursor: 'pointer', fontFamily: 'inherit' }}>
          + Add Band
        </button>
      </div>

      {rows.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          No bands. The table prints its heading and column headings only &mdash; add a band, or
          reset to the standard scale.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rows.map((r, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ flex: '0 0 48px', textAlign: 'center', fontSize: 12, color: 'var(--text-muted)' }}>{i + 1}</span>
              <input value={r.severity || ''} placeholder="Band name" aria-label={`Band ${i + 1} name`}
                onChange={e => edit(i, 'severity', e.target.value)}
                style={{ ...field, flex: '0 0 20%' }} />
              <ColorPicker value={riskRatingColor(r)} allowNone={false}
                label={`Band ${i + 1} colour`}
                onChange={c => edit(i, 'color', c)} />
              <input value={r.score || ''} placeholder="e.g. 9.0 - 10" aria-label={`Band ${i + 1} score`}
                onChange={e => edit(i, 'score', e.target.value)}
                style={{ ...field, flex: '0 0 20%' }} />
              <input value={r.description || ''} placeholder="What this band means, and how soon it must be fixed"
                aria-label={`Band ${i + 1} description`}
                onChange={e => edit(i, 'description', e.target.value)}
                style={{ ...field, flex: 1 }} />
              <button type="button" title="Remove band"
                onClick={() => setRows(rows.filter((_, n) => n !== i))}
                style={{ background: 'none', border: 'none', cursor: 'pointer',
                         color: 'var(--text-secondary)', fontSize: 16, padding: '0 6px' }}>&times;</button>
            </div>
          ))}
        </div>
      )}
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
        The band name prints reversed out of its colour, as it does in the report. A band named
        outside Critical/High/Medium/Low/Info has no house colour to fall back on, so set one.
      </div>
    </div>
  );
}

/**
 * Column headings for a table whose rows come from the engagement.
 *
 * Scope and Findings Summary are filled from scope_items and the engagement's
 * findings, so their rows are data and stay data. Their column NAMES are not:
 * 'Target' is 'Host' or 'Asset' or 'In-Scope Item' depending on whose report
 * it is, and until this existed the only way to change one was to drop the
 * band for a Custom Table and lose the data binding with it.
 *
 * An empty box is a real choice and prints an empty heading. The placeholder
 * shows the house wording so a blank box still reads as "unset", and the
 * resolvers fall back on a missing value rather than an empty one -- which is
 * what lets a template saved before this keep the heading it always printed.
 */
function ColumnLabelFields({ config, update, defaults, keys, note }) {
  const labels = resolveColumnLabels(defaults, config.columnLabels);
  const setLabel = (key, value) =>
    update('columnLabels', { ...(config.columnLabels || {}), [key]: value });

  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6 }}>
        Column Headings
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {keys.map(key => (
          <input key={key} value={labels[key]} placeholder={defaults[key]}
            aria-label={`Heading for the ${defaults[key] || key} column`}
            onChange={e => setLabel(key, e.target.value)}
            style={{
              flex: key === '#' ? '0 0 48px' : '1 1 110px', minWidth: 0,
              padding: '6px 10px', fontSize: 13, borderRadius: 6,
              border: '1px solid var(--border)', background: 'var(--bg-card)',
              color: 'var(--text)', fontFamily: 'inherit', boxSizing: 'border-box',
              textAlign: key === '#' ? 'center' : 'left',
            }} />
        ))}
      </div>
      {note && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>{note}</div>
      )}
    </div>
  );
}

function CustomColumns({ config, update }) {
  const columns = config.customColumns || [];
  const setColumns = next => update('customColumns', next);
  const edit = (i, key, value) => setColumns(columns.map((c, n) => (n === i ? { ...c, [key]: value } : c)));

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>Additional Columns</span>
        <button type="button" onClick={() => setColumns([...columns, { label: '', value: '' }])}
          style={{ padding: '4px 10px', fontSize: 12, fontWeight: 600, borderRadius: 'var(--radius)',
                   background: 'var(--bg-input)', color: 'var(--text-secondary)',
                   border: '1px solid var(--border)', cursor: 'pointer', fontFamily: 'inherit' }}>
          + Add Column
        </button>
      </div>

      {columns.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          None. Add one for a column the built-ins don't cover — leave its value empty for a blank
          column to fill in by hand, or use variables such as %CLIENTNAME% or %FINDINGSTATUS%.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {columns.map((c, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input value={c.label || ''} placeholder="Column name"
                onChange={e => edit(i, 'label', e.target.value)}
                style={{ flex: '0 0 34%', padding: '6px 10px', fontSize: 13, borderRadius: 6,
                         border: '1px solid var(--border)', background: 'var(--bg-card)',
                         color: 'var(--text)', fontFamily: 'inherit' }} />
              <input value={c.value || ''} placeholder="Leave blank, or e.g. %FINDINGOWNER%"
                onChange={e => edit(i, 'value', e.target.value)}
                style={{ flex: 1, padding: '6px 10px', fontSize: 13, borderRadius: 6,
                         border: '1px solid var(--border)', background: 'var(--bg-card)',
                         color: 'var(--text)', fontFamily: 'inherit' }} />
              <button type="button" title="Remove column"
                onClick={() => setColumns(columns.filter((_, n) => n !== i))}
                style={{ background: 'none', border: 'none', cursor: 'pointer',
                         color: 'var(--text-secondary)', fontSize: 16, padding: '0 6px' }}>×</button>
            </div>
          ))}
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            Per-finding variables: %FINDINGNUMBER% %FINDINGTITLE% %FINDINGSEVERITY% %FINDINGCVSS%
            %FINDINGSTATUS% %FINDINGUID% %FINDINGEXPLOITABLE% %FINDINGOWNER% %FINDINGENDPOINTS%
            %FINDINGORIGINALSEVERITY% %FINDINGORIGINALSTATUS% %FINDINGNEWSTATUS% —
            plus any report variable.
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Width and placement for a table section.
 *
 * Full width is the right default for a findings summary, but a two- or
 * three-column table stretched across the page reads as a layout accident —
 * acres of empty cell beside short values. Narrowing it is a presentation
 * choice, so it lives next to the table's own settings.
 */
function TableWidthFields({ config, update }) {
  const width = Number(config.tableWidth) || 100;
  const align = config.tableAlign || 'left';
  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 4 }}>
        Table Width
      </div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
        {[100, 75, 50, 35].map(pct => (
          <button key={pct} type="button" onClick={() => update('tableWidth', pct)} style={{
            padding: '4px 10px', fontSize: 12, fontWeight: 600, flex: 1,
            background: width === pct ? 'var(--primary)' : 'var(--bg-input)',
            color: width === pct ? '#fff' : 'var(--text)',
            border: '1px solid var(--border)', borderRadius: 'var(--radius)',
            cursor: 'pointer', fontFamily: 'inherit',
          }}>{pct}%</button>
        ))}
      </div>
      {width < 100 && (
        <div style={{ display: 'flex', gap: 4 }}>
          {[['left', 'Left'], ['center', 'Center'], ['right', 'Right']].map(([val, lbl]) => (
            <button key={val} type="button" onClick={() => update('tableAlign', val)} style={{
              padding: '4px 10px', fontSize: 12, fontWeight: 600, flex: 1,
              background: align === val ? 'var(--primary)' : 'var(--bg-input)',
              color: align === val ? '#fff' : 'var(--text)',
              border: '1px solid var(--border)', borderRadius: 'var(--radius)',
              cursor: 'pointer', fontFamily: 'inherit',
            }}>{lbl}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function SectionConfig({ section, onUpdate, dispatch }) {
  const { type, config } = section;
  const update = (key, value) => onUpdate({ config: { ...config, [key]: value } });
  // Several keys in one go. Calling update() twice in a tick would have the
  // second overwrite the first, because both spread the same stale config.
  const updateMany = (patch) => onUpdate({ config: { ...config, ...patch } });

  switch (type) {
    case 'title-page':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* Every line on the cover: shown or not, and saying what you want.
              The client name and engagement type used to be neither -- they
              rendered unconditionally with no way to edit or remove them. */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={config.showLogo !== false}
              onChange={e => update('showLogo', e.target.checked)} />
            Show Logo
          </label>

          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
                        color: 'var(--text-secondary)', marginTop: 4 }}>Cover lines</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: -4, marginBottom: 2 }}>
            Untick to leave a line off. Variables work here, so a line is left blank
            automatically when what it refers to is empty.
          </div>

          {[
            ['showTitle', 'titleText', 'Title', 'Penetration Testing Report'],
            ['showClient', 'clientText', 'Client', '%CLIENTNAME%'],
            ['showEngagementType', 'engagementTypeText', 'Engagement type', '%ENGAGEMENTTYPE%'],
            ['showDate', 'dateText', 'Dates', 'Date: %STARTDATE% - %ENDDATE%'],
            ['showTester', 'testerText', 'Tester', 'Tester: %TESTERNAME%'],
            ['showConfidential', 'confidentialText', 'Confidential', 'CONFIDENTIAL'],
          ].map(([showKey, textKey, label, placeholder]) => {
            const shown = config[showKey] !== false;
            return (
              <div key={showKey} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13,
                                cursor: 'pointer', width: 140, flexShrink: 0 }}>
                  <input type="checkbox" checked={shown}
                    onChange={e => update(showKey, e.target.checked)} />
                  {label}
                </label>
                <input
                  type="text"
                  value={config[textKey] || ''}
                  placeholder={placeholder}
                  disabled={!shown}
                  onChange={e => update(textKey, e.target.value)}
                  style={{
                    flex: 1, minWidth: 0, padding: '6px 8px', fontSize: 13,
                    border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                    background: 'var(--bg-input)', color: 'var(--text)',
                    fontFamily: 'inherit', opacity: shown ? 1 : 0.4,
                  }}
                />
              </div>
            );
          })}
          <div style={{ marginTop: 4 }}>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Title Page Logo</label>
            {config.customLogo && (
              <div style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                <img src={config.customLogo} alt="Title Logo" style={{ maxHeight: 50, maxWidth: 150, borderRadius: 4, border: '1px solid var(--border)' }} />
                <button onClick={() => update('customLogo', null)}
                  style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 4, cursor: 'pointer', color: '#ef4444', fontSize: 11, padding: '2px 8px' }}>Remove</button>
              </div>
            )}
            <input type="file" accept="image/*" onChange={e => {
              const file = e.target.files[0];
              if (!file) return;
              if (file.size > 2 * 1024 * 1024) { alert('Image must be under 2MB'); return; }
              const reader = new FileReader();
              reader.onload = () => update('customLogo', reader.result);
              reader.readAsDataURL(file);
              e.target.value = '';
            }} style={{ fontSize: 12 }} />
            <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 2 }}>Overrides the template logo on the title page</div>
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <Input label="Logo Width (px)" type="number" min="20" max="600" value={config.logoWidth || 120} onChange={e => update('logoWidth', parseInt(e.target.value) || 120)} style={{ marginBottom: 0, flex: 1 }} />
              <Input label="Logo Height (px)" type="number" min="20" max="400" value={config.logoHeight || 100} onChange={e => update('logoHeight', parseInt(e.target.value) || 100)} style={{ marginBottom: 0, flex: 1 }} />
            </div>
          </div>
          <TextArea label="Disclosure Text (bottom of cover page)" value={config.disclosureText || ''} placeholder="Optional disclosure statement shown at the bottom of the title page..."
            onChange={e => update('disclosureText', e.target.value)} style={{ marginBottom: 0, fontSize: 12 }} />
          <div style={{ marginTop: 8, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>ADDITIONAL CONTENT</div>
            <CompositeSection sectionId={section.id} components={config.components || []} dispatch={dispatch} />
          </div>
        </div>
      );

    case 'rich-text':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <HeadingInput config={config} update={update} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Font:</span>
            <select value={config.font || ''} onChange={e => update('font', e.target.value || null)}
              style={{ padding: '4px 8px', fontSize: 12, borderRadius: 'var(--radius)',
                       border: '1px solid var(--border)', background: 'var(--bg-input)',
                       color: 'var(--text)', cursor: 'pointer' }}>
              {FONT_OPTIONS_INHERIT.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Font Size:</span>
            <select
              value={config.fontSize || ''}
              onChange={e => update('fontSize', e.target.value ? Number(e.target.value) : null)}
              style={{
                padding: '4px 8px', fontSize: 12, borderRadius: 'var(--radius)',
                border: '1px solid var(--border)', background: 'var(--bg-input)',
                color: 'var(--text)', cursor: 'pointer'
              }}
            >
              <option value="">Default (template)</option>
              <option value="8">8pt</option>
              <option value="9">9pt</option>
              <option value="10">10pt</option>
              <option value="11">11pt</option>
              <option value="12">12pt</option>
              <option value="13">13pt</option>
              <option value="14">14pt</option>
              <option value="16">16pt</option>
              <option value="18">18pt</option>
              <option value="20">20pt</option>
              <option value="24">24pt</option>
            </select>
            {/* A block background: paragraph shading in the DOCX, a drawn band
                in the PDF. Clearing it means no background at all rather than
                white, which matters on a coloured page. */}
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Background:</span>
            <ColorPicker label="Text background" size={20}
              value={config.backgroundColor || null}
              onChange={c => update('backgroundColor', c)} />
            {config.backgroundColor && (
              <button type="button" onClick={() => update('backgroundColor', null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12,
                         color: 'var(--text-secondary)', textDecoration: 'underline', padding: 0 }}>Clear</button>
            )}
          </div>
          <RichTextEditor content={config.html || ''} onChange={html => update('html', html)} />
        </div>
      );

    case 'severity-dashboard':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <HeadingInput config={config} update={update} />
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>Chart Type:</span>
            {[['pie', 'Pie Chart'], ['bar', 'Bar Chart']].map(([val, label]) => (
              <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, cursor: 'pointer' }}>
                <input type="radio" name={`dashboard-chart-${section.id}`} checked={(config.chartType || 'pie') === val}
                  onChange={() => update('chartType', val)} />
                {label}
              </label>
            ))}
          </div>
        </div>
      );

    case 'risk-rating-table':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <HeadingInput config={config} update={update} />
          <RiskRatingRows config={config} update={update} updateMany={updateMany} />
          <TableWidthFields config={config} update={update} />
        </div>
      );

    case 'scope-table':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <HeadingInput config={config} update={update} />
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontStyle: 'italic' }}>
            Rows are populated from engagement scope data at generation time.
          </div>
          <ColumnLabelFields config={config} update={update}
            defaults={SCOPE_TABLE_LABELS} keys={['type', 'target', 'description']} />
          <Input label="When the engagement has no scope items"
            value={config.emptyText ?? ''} placeholder={SCOPE_TABLE_EMPTY}
            onChange={e => update('emptyText', e.target.value)} style={{ marginBottom: 0 }} />
          <TableWidthFields config={config} update={update} />
        </div>
      );

    case 'table-of-contents':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <HeadingInput config={config} update={update} />
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontStyle: 'italic' }}>
            Auto-generated from section headings.
          </div>
        </div>
      );

    case 'findings-summary':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <HeadingInput config={config} update={update} />
          <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' }}>Columns</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {/* Captions are the headings these columns will actually carry.
                They used to be their own wording, so the panel offered 'Title'
                and 'Directly Exploitable' for columns every renderer printed
                as 'Finding' and 'Exploitable'. */}
            {Object.keys(FINDINGS_SUMMARY_LABELS).map(key => {
              const shown = resolveColumnLabels(FINDINGS_SUMMARY_LABELS, config.columnLabels)[key];
              return (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={findingsSummaryColumns(config).includes(key)}
                    onChange={e => {
                      const cols = findingsSummaryColumns(config);
                      update('columns', e.target.checked ? [...cols, key] : cols.filter(c => c !== key));
                    }} />
                  {shown.trim() === '' ? <em style={{ color: 'var(--text-muted)' }}>unnamed</em> : shown}
                </label>
              );
            })}
          </div>
          <ColumnLabelFields config={config} update={update}
            defaults={FINDINGS_SUMMARY_LABELS}
            keys={Object.keys(FINDINGS_SUMMARY_LABELS).filter(k => findingsSummaryColumns(config).includes(k))}
            note={findingsSummaryColumns(config).length === 0
              ? 'Tick a column above to name it.'
              : 'Only the columns you are showing can be named. Each column is sized from the heading it carries.'} />
          <CustomColumns config={config} update={update} />
          <TableWidthFields config={config} update={update} />
        </div>
      );

    case 'findings-detail':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <HeadingInput config={config} update={update} />
          <div style={{ display: 'flex', gap: 12 }}>
            {[['showEvidence', 'Include Evidence/Screenshots'], ['onePerPage', 'One Finding Per Page']].map(([key, label]) => (
              <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={config[key] !== false} onChange={e => update(key, e.target.checked)} />
                {label}
              </label>
            ))}
          </div>
          <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginTop: 4 }}>Visible Fields</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {[['severity', 'Severity'], ['cvss', 'CVSS Score'], ['details', 'Details'], ['affectedEndpoints', 'Endpoints'],
              ['observation', 'Observation'], ['stepsToReproduce', 'Steps to Reproduce'], ['evidence', 'Evidence'],
              ['recommendation', 'Recommendation'], ['references', 'References']].map(([key, label]) => (
              <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={(config.showFields || []).includes(key)}
                  onChange={e => {
                    const fields = config.showFields || [];
                    update('showFields', e.target.checked ? [...fields, key] : fields.filter(f => f !== key));
                  }} />
                {label}
              </label>
            ))}
          </div>
        </div>
      );

    case 'heading':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Level:</span>
              {[1, 2, 3].map(lvl => (
                <button key={lvl} onClick={() => update('level', lvl)} style={{
                  padding: '4px 12px', fontSize: 12, fontWeight: 600,
                  background: config.level === lvl ? 'var(--primary)' : 'var(--bg-input)',
                  color: config.level === lvl ? '#fff' : 'var(--text)',
                  border: '1px solid var(--border)', borderRadius: 'var(--radius)', cursor: 'pointer'
                }}>H{lvl}</button>
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Align:</span>
              {['left', 'center', 'right'].map(a => (
                <button key={a} onClick={() => update('align', a)} style={{
                  padding: '4px 12px', fontSize: 12, fontWeight: 600, textTransform: 'capitalize',
                  background: (config.align || 'left') === a ? 'var(--primary)' : 'var(--bg-input)',
                  color: (config.align || 'left') === a ? '#fff' : 'var(--text)',
                  border: '1px solid var(--border)', borderRadius: 'var(--radius)', cursor: 'pointer'
                }}>{a}</button>
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Colour:</span>
              {/* Applies to the whole heading on both the plain and the rich-text
                  branch, so it keeps working after someone types in the editor
                  below. Colour set on a selection inside the heading still wins. */}
              <ColorPicker label="Heading colour" size={20}
                value={config.color || null} onChange={c => update('color', c)} />
            </div>
          </div>
          <RichTextEditor content={config.html || config.text || ''} onChange={html => update('html', html)} />
        </div>
      );

    case 'text-block':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Font:</span>
            <select value={config.font || ''} onChange={e => update('font', e.target.value || null)}
              style={{ padding: '4px 8px', fontSize: 12, borderRadius: 'var(--radius)',
                       border: '1px solid var(--border)', background: 'var(--bg-input)',
                       color: 'var(--text)', cursor: 'pointer' }}>
              {FONT_OPTIONS_INHERIT.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Font Size:</span>
            <select
              value={config.fontSize || ''}
              onChange={e => update('fontSize', e.target.value ? Number(e.target.value) : null)}
              style={{
                padding: '4px 8px', fontSize: 12, borderRadius: 'var(--radius)',
                border: '1px solid var(--border)', background: 'var(--bg-input)',
                color: 'var(--text)', cursor: 'pointer'
              }}
            >
              <option value="">Default (template)</option>
              <option value="8">8pt</option>
              <option value="9">9pt</option>
              <option value="10">10pt</option>
              <option value="11">11pt</option>
              <option value="12">12pt</option>
              <option value="13">13pt</option>
              <option value="14">14pt</option>
              <option value="16">16pt</option>
              <option value="18">18pt</option>
              <option value="20">20pt</option>
              <option value="24">24pt</option>
            </select>
            {/* A block background: paragraph shading in the DOCX, a drawn band
                in the PDF. Clearing it means no background at all rather than
                white, which matters on a coloured page. */}
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Background:</span>
            <ColorPicker label="Text background" size={20}
              value={config.backgroundColor || null}
              onChange={c => update('backgroundColor', c)} />
            {config.backgroundColor && (
              <button type="button" onClick={() => update('backgroundColor', null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12,
                         color: 'var(--text-secondary)', textDecoration: 'underline', padding: 0 }}>Clear</button>
            )}
          </div>
          <RichTextEditor content={config.html || ''} onChange={html => update('html', html)} />
        </div>
      );

    case 'custom-table':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <HeadingInput config={config} update={update} />
          <CustomTableEditor
            columns={config.columns || []}
            rows={config.rows || []}
            rowStyles={config.rowStyles || []}
            tableStyle={config.tableStyle || {}}
            cellStyles={config.cellStyles || []}
            columnStyles={config.columnStyles || []}
            onChange={(columns, rows, rowStyles, tableStyle, cellStyles, columnStyles) => onUpdate({ config: { ...config, columns, rows, rowStyles, tableStyle, cellStyles, columnStyles } })}
          />
          <TableWidthFields config={config} update={update} />
        </div>
      );

    case 'image': {
      const handleImageUpload = (ev) => {
        const file = ev.target.files[0];
        if (!file) return;
        if (file.size > 2 * 1024 * 1024) {
          alert('Image is larger than 2 MB. Please use a smaller image.');
          return;
        }
        const reader = new FileReader();
        reader.onload = () => {
          const data = reader.result;
          // This section and the Custom Section's image component share
          // renderImage in docx-report.js, which needs the image's own
          // proportions. Only the composite path used to measure them, so an
          // image placed here still came out as if it were 4:3 -- squashing
          // every screenshot, which is most of what goes in one.
          const probe = new window.Image();
          probe.onload = () => {
            const nW = probe.naturalWidth || 0;
            const nH = probe.naturalHeight || 0;
            const fit = Math.min(1, nW ? 600 / nW : 1, nH ? 900 / nH : 1);
            updateMany({
              imageData: data,
              imageNaturalWidth: nW || null,
              imageNaturalHeight: nH || null,
              imageWidthPx: nW ? Math.max(10, Math.round(nW * fit)) : null,
              imageHeightPx: nH ? Math.max(10, Math.round(nH * fit)) : null,
            });
          };
          probe.onerror = () => update('imageData', data);
          probe.src = data;
        };
        reader.readAsDataURL(file);
      };

      const imgNatW = Number(config.imageNaturalWidth) || null;
      const imgNatH = Number(config.imageNaturalHeight) || null;
      // Templates saved before these boxes existed open at the size they
      // already render at, not at a number nobody chose. 600px is the
      // renderer's full text-width reference; 0.75 is the ratio it assumed
      // before anything measured the image.
      const imgLegacyPct = Math.min(100, Math.max(10, Number(config.width) || 80));
      const imgPxW = Number(config.imageWidthPx) || Math.round(600 * imgLegacyPct / 100);
      const imgPxH = Number(config.imageHeightPx)
                  || Math.round(imgPxW * ((imgNatW && imgNatH) ? imgNatH / imgNatW : 0.75));

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <HeadingInput config={config} update={update} />
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>Image</label>
            <input type="file" accept="image/*" onChange={handleImageUpload} style={{ fontSize: 12 }} />
          </div>
          {config.imageData && (
            <div style={{ position: 'relative', maxWidth: 200 }}>
              <img src={config.imageData} alt="Preview" style={{ maxWidth: '100%', borderRadius: 'var(--radius)', border: '1px solid var(--border)' }} />
              <button onClick={() => update('imageData', null)} style={{
                position: 'absolute', top: 4, right: 4, background: '#ef4444', color: '#fff',
                border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer',
                fontSize: 12, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>x</button>
            </div>
          )}
          <Input label="Caption" value={config.caption || ''} onChange={e => update('caption', e.target.value)} style={{ marginBottom: 0 }} />

          {/* Same two boxes as the Custom Section's image component and the
              header config, because they all feed one renderer. Without them
              the measured size set on upload would be unreachable: picked for
              the user, editable by nobody. */}
          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
            <Input label="W (px)" type="number" min="10" max="600" value={imgPxW}
              onChange={e => update('imageWidthPx', parseInt(e.target.value, 10) || null)}
              style={{ marginBottom: 0, flex: 1 }} />
            <Input label="H (px)" type="number" min="10" max="900" value={imgPxH}
              onChange={e => update('imageHeightPx', parseInt(e.target.value, 10) || null)}
              style={{ marginBottom: 0, flex: 1 }} />
            {imgNatW && imgNatH && (
              <button type="button"
                onClick={() => update('imageHeightPx', Math.max(10, Math.round(imgPxW * imgNatH / imgNatW)))}
                title={`Source is ${imgNatW}×${imgNatH}px`}
                style={{ padding: '6px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                         background: 'var(--bg-input)', color: 'var(--text)', whiteSpace: 'nowrap',
                         border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                Fit to source
              </button>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Alignment:</span>
            {['left', 'center', 'right'].map(a => (
              <button key={a} onClick={() => update('align', a)} style={{
                padding: '4px 12px', fontSize: 12, fontWeight: 600, textTransform: 'capitalize',
                background: (config.align || 'center') === a ? 'var(--primary)' : 'var(--bg-input)',
                color: (config.align || 'center') === a ? '#fff' : 'var(--text)',
                border: '1px solid var(--border)', borderRadius: 'var(--radius)', cursor: 'pointer'
              }}>{a}</button>
            ))}
          </div>
        </div>
      );
    }

    case 'severity-chart':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <HeadingInput config={config} update={update} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Chart Type:</span>
            {['pie', 'bar'].map(ct => (
              <button key={ct} onClick={() => update('chartType', ct)} style={{
                padding: '4px 12px', fontSize: 12, fontWeight: 600, textTransform: 'capitalize',
                background: config.chartType === ct ? 'var(--primary)' : 'var(--bg-input)',
                color: config.chartType === ct ? '#fff' : 'var(--text)',
                border: '1px solid var(--border)', borderRadius: 'var(--radius)', cursor: 'pointer'
              }}>{ct}</button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontStyle: 'italic' }}>
            Auto-generated from finding severity counts at report generation time.
          </div>
        </div>
      );

    case 'composite':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <HeadingInput config={config} update={update} />
          {/* Heading colour lives here rather than inside HeadingInput, which
              ten block types share. Putting it there would render a control on
              nine other blocks whose renderers never read it -- a setting that
              saves and does nothing is worse than no setting.

              Unset deliberately means "leave each renderer alone", because the
              three currently disagree: Word paints the composite heading with
              its own Heading 1 theme colour, the PDF draws it black, and the
              preview uses slate. Any unconditional default here would repaint
              every composite heading in every template already saved. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Heading Colour:</span>
            <ColorPicker label="Heading colour" size={20}
              value={config.headingColor || null}
              onChange={c => update('headingColor', c)} />
            {config.headingColor && (
              <button type="button" onClick={() => update('headingColor', null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12,
                         color: 'var(--text-secondary)', textDecoration: 'underline', padding: 0 }}>Clear</button>
            )}
          </div>
          <CompositeSection sectionId={section.id} components={config.components || []} dispatch={dispatch} />
        </div>
      );

    case 'spacer':
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Height:</span>
          <input type="number" min={1} max={400} step={1} value={config.height ?? 20}
            onChange={e => update('height', Math.min(400, Math.max(1, Number(e.target.value) || 1)))}
            style={{ width: 70, padding: '4px 8px', fontSize: 12, borderRadius: 'var(--radius)',
                     border: '1px solid var(--border)', background: 'var(--bg-input)',
                     color: 'var(--text)', fontFamily: 'inherit' }} />
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>pt</span>
          <input type="range" min={1} max={200} value={Math.min(200, config.height ?? 20)}
            onChange={e => update('height', Number(e.target.value))}
            style={{ flex: 1, minWidth: 80, maxWidth: 160 }} />
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            about {(((config.height ?? 20) / 72) * 25.4).toFixed(0)}mm of blank space
          </span>
        </div>
      );

    case 'findings-walkthrough':
      return <FindingsWalkthroughConfig config={config} update={update} />;
    case 'revalidation-summary':
      return <RevalidationSummaryConfig config={config} update={update} />;
    case 'owasp-findings':
      return <OwaspFindingsConfig config={config} update={update} />;
    case 'finding-cards':
      return <FindingCardsConfig config={config} update={update} />;

    case 'metric-tiles': {
      const tiles = Array.isArray(config.tiles) ? config.tiles : [];
      const setTiles = next => update('tiles', next);
      const setTile = (i, k, v) => setTiles(tiles.map((t, n) => n === i ? { ...t, [k]: v } : t));
      const addTile = () => setTiles([...tiles, { label: 'Label', value: '', bg: '#f3f4f6',
                                                  labelColor: '#666666', valueColor: '#1f3864' }]);
      const move = (i, d) => {
        const n = i + d;
        if (n < 0 || n >= tiles.length) return;
        const next = tiles.slice();
        [next[i], next[n]] = [next[n], next[i]];
        setTiles(next);
      };
      const inp = {
        padding: '4px 8px', fontSize: 12, borderRadius: 'var(--radius)', boxSizing: 'border-box',
        border: '1px solid var(--border)', background: 'var(--bg-input)',
        color: 'var(--text)', fontFamily: 'inherit', minWidth: 0,
      };
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <input value={config.heading || ''} placeholder="Heading &mdash; blank for none"
            onChange={e => update('heading', e.target.value)} style={{ ...inp, width: '100%%' }} />

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Per row:</span>
            <input type="number" min={1} max={8} value={config.perRow || tiles.length || 1}
              title="How many tiles sit on one line before wrapping"
              onChange={e => update('perRow', Math.max(1, Number(e.target.value) || 1))}
              style={{ ...inp, width: 56 }} />
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Label:</span>
            <input type="number" min={5} max={20} value={config.labelSize || 8}
              onChange={e => update('labelSize', Number(e.target.value) || 8)} style={{ ...inp, width: 52 }} />
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Value:</span>
            <input type="number" min={6} max={48} value={config.valueSize || 16}
              onChange={e => update('valueSize', Number(e.target.value) || 16)} style={{ ...inp, width: 52 }} />
            {/* ?? and not ||: an author who wants the caption tight against
                the figure stores 0, and || would show it back as the default. */}
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Gap:</span>
            <input type="number" min={0} max={24} value={config.labelGap ?? METRIC_TILE_LABEL_GAP}
              title="Points between the caption and the figure"
              onChange={e => update('labelGap', Math.min(24, Math.max(0, Number(e.target.value) || 0)))}
              style={{ ...inp, width: 52 }} />
            {/* The band's alignment, which every row follows unless it sets
                its own. One of the three and never blank: this IS the default
                the rows fall back to. */}
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Align:</span>
            <select value={metricTileAlign({}, config)}
              title="How every row is aligned, unless a row sets its own"
              onChange={e => update('tileAlign', e.target.value)}
              style={{ ...inp, width: 80, cursor: 'pointer' }}>
              {METRIC_TILE_ALIGNMENTS.map(a => (
                <option key={a} value={a}>{a[0].toUpperCase() + a.slice(1)}</option>
              ))}
            </select>
            <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12,
                            color: 'var(--text-secondary)', cursor: 'pointer' }}>
              <input type="checkbox" checked={config.upperCaseLabels !== false}
                onChange={e => update('upperCaseLabels', e.target.checked)} style={{ margin: 0, cursor: 'pointer' }} />
              Caps
            </label>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {tiles.length === 0 && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                No tiles yet. A tile is a caption over a figure &mdash; the value takes report variables.
              </div>
            )}
            {tiles.map((t, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap',
                                    padding: 5, borderRadius: 'var(--radius)',
                                    border: '1px solid var(--border)', background: 'var(--bg-input)' }}>
                <input value={t.label || ''} placeholder="Label"
                  onChange={e => setTile(i, 'label', e.target.value)} style={{ ...inp, flex: '1 1 90px' }} />
                <input value={t.value || ''} placeholder="Value or %CRITICALCOUNT%"
                  onChange={e => setTile(i, 'value', e.target.value)} style={{ ...inp, flex: '1 1 120px' }} />
                <ColorPicker label="Tile background" size={20} value={t.bg || null}
                  onChange={c => setTile(i, 'bg', c)} />
                <ColorPicker label="Label colour" size={20} allowNone={false}
                  value={t.labelColor || '#666666'} onChange={c => setTile(i, 'labelColor', c || '#666666')} />
                <ColorPicker label="Value colour" size={20} allowNone={false}
                  value={t.valueColor || '#1f3864'} onChange={c => setTile(i, 'valueColor', c || '#1f3864')} />
                {/* Blank is a real choice here and the default one: it means
                    "follow the band", so changing the band moves this row
                    with it. Storing the band's current value instead would
                    pin the row silently. */}
                <select value={METRIC_TILE_ALIGNMENTS.includes(t.align) ? t.align : ''}
                  title="Row alignment — blank follows the band"
                  onChange={e => setTile(i, 'align', e.target.value)}
                  style={{ ...inp, width: 78, cursor: 'pointer' }}>
                  <option value="">Band</option>
                  {METRIC_TILE_ALIGNMENTS.map(a => (
                    <option key={a} value={a}>{a[0].toUpperCase() + a.slice(1)}</option>
                  ))}
                </select>
                <button type="button" title="Move left" onClick={() => move(i, -1)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '0 2px' }}>&lsaquo;</button>
                <button type="button" title="Move right" onClick={() => move(i, 1)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '0 2px' }}>&rsaquo;</button>
                <button type="button" title="Remove" onClick={() => setTiles(tiles.filter((_, n) => n !== i))}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 15, padding: '0 3px' }}>&times;</button>
              </div>
            ))}
            <button type="button" onClick={addTile}
              style={{ alignSelf: 'flex-start', padding: '3px 9px', fontSize: 12, fontWeight: 600,
                       borderRadius: 'var(--radius)', background: 'var(--bg-input)',
                       color: 'var(--text-secondary)', border: '1px solid var(--border)',
                       cursor: 'pointer', fontFamily: 'inherit' }}>
              + Tile
            </button>
          </div>
        </div>
      );
    }

    case 'version-history': {
      const ts = config.tableStyle || {};
      const setTs = (k, v) => update('tableStyle', { ...ts, [k]: v });
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <HeadingInput config={config} update={update} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12,
                          color: 'var(--text-secondary)', cursor: 'pointer' }}>
            <input type="checkbox" checked={config.allTemplates === true}
              onChange={e => update('allTemplates', e.target.checked)}
              style={{ cursor: 'pointer', margin: 0 }} />
            List every report produced for this engagement
          </label>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: -5 }}>
            {config.allTemplates
              ? 'Every template, with a Report column naming each. Versions restart per template, so the same number can appear more than once.'
              : 'Only this template’s own versions — which is what the version on the cover refers to.'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <ColorPicker value={ts.headerBg || '#f3f4f6'} size={24} label="Header background"
              onChange={v => setTs('headerBg', v || '#f3f4f6')} />
            <ColorPicker value={ts.headerTextColor || '#000000'} size={24} label="Header text"
              onChange={v => setTs('headerTextColor', v || '#000000')} />
            <ColorPicker value={ts.borderColor || '#e5e7eb'} size={24} label="Borders"
              onChange={v => setTs('borderColor', v || '#e5e7eb')} />
            <select value={ts.font || ''} onChange={e => setTs('font', e.target.value || null)}
              title="Table font"
              style={{ padding: '3px 6px', fontSize: 11, borderRadius: 'var(--radius)',
                       border: '1px solid var(--border)', background: 'var(--bg-input)',
                       color: 'var(--text)', cursor: 'pointer' }}>
              {FONT_OPTIONS_INHERIT.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
            <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12,
                            color: 'var(--text-secondary)', cursor: 'pointer' }}>
              <input type="checkbox" checked={ts.showHeader !== false}
                onChange={e => setTs('showHeader', e.target.checked)}
                style={{ cursor: 'pointer', margin: 0 }} />
              Column header
            </label>
          </div>
        </div>
      );
    }

    case 'horizontal-line':
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Color:</span>
            <ColorPicker label="Line colour" size={24}
              value={config.color || '#cccccc'}
              onChange={c => update('color', c || '#cccccc')} />
            <button onClick={() => update('color', '#cccccc')} style={{ fontSize: 11, color: 'var(--text-secondary)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Reset</button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Thickness:</span>
            <input type="range" min={1} max={10} value={config.thickness || 1}
              onChange={e => update('thickness', Number(e.target.value))}
              style={{ flex: 1 }} />
            <span style={{ fontSize: 12, fontWeight: 600, minWidth: 28 }}>{config.thickness || 1}px</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Width:</span>
            <input type="range" min={10} max={100} value={config.width || 100}
              onChange={e => update('width', Number(e.target.value))}
              style={{ flex: 1 }} />
            <span style={{ fontSize: 12, fontWeight: 600, minWidth: 28 }}>{config.width || 100}%</span>
          </div>
          <div style={{ borderTop: `${config.thickness || 1}px solid ${config.color || '#cccccc'}`, width: `${config.width || 100}%`, margin: '4px auto' }} />
        </div>
      );

    case 'page-break':
      return (
        <div style={{ textAlign: 'center', padding: '8px 0', color: 'var(--text-secondary)', fontSize: 12, fontStyle: 'italic' }}>
          Inserts a page break in the PDF output.
        </div>
      );

    default:
      return null;
  }
}

export default function SectionBlock({ section, onUpdate, onRemove, dispatch, onSaveSection, collapseSignal }) {
  const [collapsed, setCollapsed] = useState(false);
  // Collapse All broadcasts; a block still owns its own state, so opening one
  // by hand is not undone by the next render.
  useEffect(() => {
    if (collapseSignal && collapseSignal.nonce) setCollapsed(collapseSignal.collapsed);
  }, [collapseSignal]);
  const [editingLabel, setEditingLabel] = useState(false);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    marginBottom: 8,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div style={{
        border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)',
        background: 'var(--bg-card)', overflow: 'hidden',
        boxShadow: isDragging ? 'var(--shadow-lg)' : 'none',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
          background: 'var(--bg-input)', borderBottom: collapsed ? 'none' : '1px solid var(--border)',
          cursor: 'pointer', userSelect: 'none'
        }}>
          {/* Drag handle */}
          <div {...attributes} {...listeners} style={{ cursor: 'grab', padding: '2px 4px', color: 'var(--text-secondary)', fontSize: 16, lineHeight: 1 }} title="Drag to reorder">
            ≡
          </div>
          <span style={{ fontSize: 16, lineHeight: 1 }}>{SECTION_ICONS[section.type] || '📄'}</span>
          {editingLabel ? (
            <input
              autoFocus
              value={section.label}
              onChange={e => onUpdate({ label: e.target.value })}
              onBlur={() => setEditingLabel(false)}
              onKeyDown={e => { if (e.key === 'Enter') setEditingLabel(false); }}
              onClick={e => e.stopPropagation()}
              style={{
                flex: 1, fontSize: 13, fontWeight: 600, padding: '2px 6px',
                border: '1px solid var(--primary)', borderRadius: 'var(--radius)',
                background: 'var(--bg)', color: 'var(--text)', outline: 'none'
              }}
            />
          ) : (
            <>
              <span
                style={{ fontSize: 13, fontWeight: 600, flex: 1, cursor: 'pointer' }}
                onClick={() => setCollapsed(!collapsed)}
                title={collapsed ? 'Expand' : 'Collapse'}
              >
                {section.label}
              </span>
              {/* Renaming used to be a double-click on the label, which could
                  not work: the label's own click collapses the section, so the
                  two clicks of a double-click shut the pane and opened it
                  again while the rename never started. The tooltip promised
                  it anyway. A button of its own competes with nothing. */}
              <button
                onClick={e => { e.stopPropagation(); setEditingLabel(true); }}
                style={{
                  background: 'none', border: '1px solid var(--border)', borderRadius: 4,
                  cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 10,
                  padding: '2px 6px', whiteSpace: 'nowrap', fontFamily: 'inherit',
                }}
                title="Rename this section"
              >{'\u270e'} Rename</button>
            </>
          )}
          {/* Page break toggle */}
          {section.type !== 'page-break' && section.type !== 'title-page' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text-secondary)', cursor: 'pointer' }}
              onClick={e => e.stopPropagation()}>
              <input type="checkbox" checked={section.config?.pageBreakBefore || false}
                onChange={e => onUpdate({ config: { ...section.config, pageBreakBefore: e.target.checked } })} />
              Page break
            </label>
          )}
          {/* Keep this section, exactly as it stands, for everyone in the
              organisation. Offered on every section: the ones most worth
              keeping are the composites someone has spent an afternoon on,
              but a Rich Text band holding the standard methodology wording is
              just as reusable. */}
          {onSaveSection && (
            <button
              onClick={e => { e.stopPropagation(); onSaveSection(section); }}
              style={{
                background: 'none', border: '1px solid var(--border)', borderRadius: 4,
                cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 10,
                padding: '2px 6px', whiteSpace: 'nowrap', fontFamily: 'inherit',
              }}
              title="Save this section to Customer Templates"
            >★ Save</button>
          )}
          <button onClick={() => setCollapsed(!collapsed)} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)',
            fontSize: 14, padding: '2px 6px', transform: collapsed ? 'rotate(-90deg)' : 'rotate(0)',
            transition: 'transform 0.2s'
          }}>▼</button>
          {dispatch && section.type !== 'title-page' && section.type !== 'table-of-contents' && section.type !== 'findings-detail' && section.type !== 'findings-summary' && section.type !== 'severity-dashboard' && section.type !== 'owasp-findings' && section.type !== 'finding-cards' && section.type !== 'revalidation-summary' && (
            <button onClick={e => { e.stopPropagation(); dispatch({ type: 'MOVE_TO_TITLE_PAGE', payload: { sectionId: section.id } }); }}
              style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 4, cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 10, padding: '2px 6px' }}
              title="Move into Title Page">→📄</button>
          )}
          <button onClick={onRemove} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444',
            fontSize: 16, padding: '2px 6px', lineHeight: 1
          }} title="Remove section">✕</button>
        </div>
        {/* Content */}
        {!collapsed && (
          <div style={{ padding: 12 }}>
            <SectionConfig section={section} onUpdate={onUpdate} dispatch={dispatch} />
          </div>
        )}
      </div>
    </div>
  );
}
